import { useState } from 'react'
import { lumi } from '@/lib/lumi'
import type { EvaluationScores } from '@/lib/evaluationAspects'
import { EVALUATION_ASPECTS } from '@/lib/evaluationAspects'

export type EvaluationDirection = 'empresa_a_colaborador' | 'colaborador_a_empresa'

export interface Evaluation {
  _id: string
  linkId?: string
  companyUserId: string
  companyName?: string
  collaboratorUserId: string
  collaboratorName?: string
  area?: string
  period: string
  scores: EvaluationScores
  average: number
  evaluationDirection: EvaluationDirection
  comments?: string
  observations?: string
  recommendations?: string
  creator?: string
  createdAt: string
  updatedAt: string
}

export function useEvaluations() {
  const [evaluations, setEvaluations] = useState<Evaluation[]>([])
  const [loading, setLoading] = useState(false)

  // Evaluaciones que la empresa hizo a sus colaboradores.
  // Incluye registros antiguos (legacy) que aún no tienen el campo evaluationDirection.
  const fetchByCompany = async (companyUserId: string) => {
    setLoading(true)
    try {
      const { list } = await lumi.entities.evaluations.list({
        filter: {
          companyUserId,
          $or: [
            { evaluationDirection: 'empresa_a_colaborador' },
            { evaluationDirection: { $exists: false } },
          ],
        },
        sort: { period: -1 },
      })
      setEvaluations(list as Evaluation[])
    } catch (e) {
      console.error('Error al cargar evaluaciones (empresa):', e)
    } finally {
      setLoading(false)
    }
  }

  // Evaluaciones recibidas por el colaborador (hechas por empresas).
  // Incluye registros antiguos (legacy) sin el campo evaluationDirection.
  const fetchByCollaborator = async (collaboratorUserId: string) => {
    setLoading(true)
    try {
      const { list } = await lumi.entities.evaluations.list({
        filter: {
          collaboratorUserId,
          $or: [
            { evaluationDirection: 'empresa_a_colaborador' },
            { evaluationDirection: { $exists: false } },
          ],
        },
        sort: { period: -1 },
      })
      setEvaluations(list as Evaluation[])
    } catch (e) {
      console.error('Error al cargar evaluaciones (colaborador):', e)
    } finally {
      setLoading(false)
    }
  }

  // Evaluaciones que los colaboradores hicieron a una empresa
  const fetchCompanyReviews = async (companyUserId: string) => {
    setLoading(true)
    try {
      const { list } = await lumi.entities.evaluations.list({
        filter: { companyUserId, evaluationDirection: 'colaborador_a_empresa' },
        sort: { createdAt: -1 },
      })
      setEvaluations(list as Evaluation[])
    } catch (e) {
      console.error('Error al cargar evaluaciones de empresa:', e)
    } finally {
      setLoading(false)
    }
  }

  // Evaluaciones que el colaborador hizo a empresas
  const fetchByCollaboratorToCompanies = async (collaboratorUserId: string) => {
    setLoading(true)
    try {
      const { list } = await lumi.entities.evaluations.list({
        filter: { collaboratorUserId, evaluationDirection: 'colaborador_a_empresa' },
        sort: { createdAt: -1 },
      })
      setEvaluations(list as Evaluation[])
    } catch (e) {
      console.error('Error al cargar evaluaciones hechas a empresas:', e)
    } finally {
      setLoading(false)
    }
  }

  const createEvaluation = async (data: Partial<Evaluation>) => {
    const now = new Date().toISOString()
    const result = await lumi.entities.evaluations.create({
      ...data,
      creator: data.creator,
      createdAt: now,
      updatedAt: now,
    })
    // Detectar habilidades < 8 y enviar notificaciones con cursos recomendados
    if (data.evaluationDirection === 'empresa_a_colaborador' && data.collaboratorUserId && data.scores) {
      try {
        const { list: coursesAll } = await lumi.entities.courses.list({ filter: { status: 'active' }, limit: 50 })
        const lowEntries = Object.entries(data.scores as Record<string, number>).filter(([, v]) => Number(v) < 8)
        for (const [skillKey] of lowEntries) {
          const aspect = EVALUATION_ASPECTS.find(a => a.key === skillKey)
          const relatedCourse = (coursesAll as any[]).find(c => c.skill === skillKey)
          const skillLabel = aspect?.label ?? skillKey
          const courseTitle = relatedCourse?.title ?? null
          await lumi.entities.notifications.create({
            title: `Nuevo curso disponible: ${skillLabel}`,
            body: courseTitle
              ? `Tienes un curso disponible para mejorar tu ${skillLabel}: "${courseTitle}". Visita tu módulo de Cursos.`
              : `Tienes áreas de oportunidad en ${skillLabel}. Revisa los cursos disponibles en tu módulo de Cursos.`,
            type: 'curso',
            targetAudience: 'colaboradores',
            targetUserId: data.collaboratorUserId,
            status: 'publicada',
            readBy: [],
            linkUrl: '/colaborador/cursos',
            creator: data.creator ?? '',
            createdAt: now,
            updatedAt: now,
          })
        }
      } catch (e) {
        console.error('Error enviando notificaciones de cursos:', e)
      }
    }
    return result
  }

  const deleteEvaluation = async (id: string) => {
    return lumi.entities.evaluations.delete(id)
  }

  return {
    evaluations,
    loading,
    fetchByCompany,
    fetchByCollaborator,
    fetchCompanyReviews,
    fetchByCollaboratorToCompanies,
    createEvaluation,
    deleteEvaluation,
  }
}
